import React, { createContext, useCallback, useContext, useRef, useState } from "react";

// ─── Map Command Types ──────────────────────────────────────────────

export type MapCommand =
  | { type: "flyTo"; lat: number; lng: number; zoom?: number }
  | { type: "highlightBA"; baCode: string }
  | { type: "highlightGenerators"; generators: Array<{ lat: number; lng: number; label?: string }> }
  | { type: "setFilters"; filters: Record<string, any> }
  | { type: "addMarker"; lat: number; lng: number; label?: string; color?: string }
  | { type: "clearHighlights" }
  | { type: "navigateTo"; path: string };

// ─── Map State (map → chat) ─────────────────────────────────────────

export interface MapState {
  page: string;
  selectedBaCode?: string | null;
  selectedGenerator?: { plantId: string; generatorId: string } | null;
  activeFilters?: Record<string, string>;
  viewMode?: string;
  zoom?: number;
}

// ─── Context Value ──────────────────────────────────────────────────

interface MapBridgeContextValue {
  mapState: MapState;
  setMapState: (state: MapState | ((prev: MapState) => MapState)) => void;

  // Command queue: chat pushes commands, map consumes them
  pendingCommands: MapCommand[];
  dispatchCommand: (cmd: MapCommand) => void;
  consumeCommands: () => MapCommand[];
}

const MapBridgeContext = createContext<MapBridgeContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────

export const MapBridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mapState, setMapState] = useState<MapState>({ page: "" });
  const commandQueueRef = useRef<MapCommand[]>([]);
  const [, setTick] = useState(0);

  const dispatchCommand = useCallback((cmd: MapCommand) => {
    commandQueueRef.current.push(cmd);
    setTick((t) => t + 1); // trigger re-render so consumers pick up
  }, []);

  const consumeCommands = useCallback(() => {
    const cmds = [...commandQueueRef.current];
    commandQueueRef.current = [];
    return cmds;
  }, []);

  return (
    <MapBridgeContext.Provider
      value={{ mapState, setMapState, pendingCommands: commandQueueRef.current, dispatchCommand, consumeCommands }}
    >
      {children}
    </MapBridgeContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────────

export function useMapBridge() {
  const ctx = useContext(MapBridgeContext);
  if (!ctx) throw new Error("useMapBridge must be used within MapBridgeProvider");
  return ctx;
}

export function useMapBridgeOptional() {
  return useContext(MapBridgeContext);
}
